// components/Sidebar.js
import React, { useState } from 'react';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Box,
    Divider,
    Tooltip,
    Typography,
    Avatar,
    IconButton,
    Link
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import MarkunreadIcon from '@mui/icons-material/Markunread';
import PlaceIcon from '@mui/icons-material/Place';
import FeedbackIcon from '@mui/icons-material/Feedback';


import {
    Dashboard as DashboardIcon,
    Category as CategoryIcon,
    History as HistoryIcon,
    Analytics as AnalyticsIcon,
    Settings as SettingsIcon,
    Help as HelpIcon,
    AccountCircle as AccountIcon,
    Notifications as NotificationsIcon,
    Search as SearchIcon,
    TrendingUp as TrendingUpIcon,
    Assessment as AssessmentIcon,
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bold } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../constants/data';

// Responsive widths handled via sx breakpoints below
const SIDEBAR_WIDTH_EXPANDED_XS = 240; // tablets/smaller laptops
const SIDEBAR_WIDTH_EXPANDED_MD = 280; // larger laptops/desktops
const SIDEBAR_WIDTH_COLLAPSED_XS = 60;
const SIDEBAR_WIDTH_COLLAPSED_MD = 72;

const navigationItems = [
    {
        id: 'dashboard',
        label: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/dashboard',
        group: 'main'
    },
    {
        id: 'comlaints',
        label: 'Complaints',
        icon: <MarkunreadIcon />,
        path: '/complaints',
        group: 'main'
    },
    {
        id: 'feedback',
        label: 'Feedback',
        icon: <FeedbackIcon />,
        path: '/feedback',
        group: 'main'
    },
    {
        id: 'category-explorer',
        label: 'Categories',
        icon: <CategoryIcon />,
        path: '/categories',
        group: 'main'
    },
    {
        id: 'companies',
        label: 'Companies',
        icon: <BusinessIcon />,
        path: '/companies',
        group: 'main'
    },
    {
        id: 'alert',
        label: 'Alerts',
        icon: <NotificationsIcon />,
        path: '/alert',
        group: 'main'
    },
    {
        id: 'semantic-search',
        label: 'Map View',
        icon: <PlaceIcon />,
        path: '/semantic-search',
        group: 'main'
    }
];

const Sidebar = ({ open, onToggle }) => {
    const [isHovered, setIsHovered] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const isExpanded = open || isHovered;



    const handleLogout = async () => {
        try {
            const token = localStorage.getItem('access_token');
            if (token) {
                await axios.post(`${API_URL}/logout`, {}, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    withCredentials: true // Important: Clear the HttpOnly cookie
                });
            }
        } catch (error) {
            console.error("Logout failed", error);
        }

        // Clear any authentication tokens or user data here
        localStorage.removeItem('access_token');
        localStorage.removeItem('alertDataCache')
        localStorage.removeItem('categoryDataCache')
        localStorage.removeItem('companyDataCache')
        localStorage.removeItem('stateDataCache')
        localStorage.removeItem('categoryAlertData')
        localStorage.removeItem('dashboardFilters');
        localStorage.removeItem('feedbackStatsCache');
        localStorage.removeItem('searchHistory');
        localStorage.removeItem("currentSubcategoryData")
        localStorage.removeItem("subAlertCache")
        localStorage.removeItem("subStateDataCache")
        localStorage.removeItem("subCompanyCache")
        // Redirect to login page
        navigate('/login');
    };

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    const handleNavigation = (path) => {
        navigate(path);
    };

    const isActiveRoute = (path) => {
        return location.pathname === path;
    };

    const renderNavigationItem = (item) => {
        if (item.type === 'divider') {
            return <Divider key={item.id} sx={{ my: 1 }} />;
        }

        const isActive = isActiveRoute(item.path);

        return (
            <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
                <Tooltip
                    title={!isExpanded ? item.label : ''}
                    placement="right"
                    arrow
                >
                    <ListItemButton
                        onClick={() => handleNavigation(item.path)}
                        sx={{
                            minHeight: { xs: 44, sm: 48 },
                            justifyContent: isExpanded ? 'initial' : 'center',
                            px: { xs: 2, sm: 2.5 },
                            backgroundColor: isActive ? 'action.selected' : 'transparent',
                            borderRight: isActive ? 3 : 0,
                            borderColor: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                                '& .MuiListItemIcon-root': {
                                    color: 'primary.main',
                                },
                                '& .MuiListItemText-primary': {
                                    fontWeight: 500,
                                    color: 'primary.main',
                                }
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: isExpanded ? 3 : 'auto',
                                justifyContent: 'center',
                                color: isActive ? 'primary.main' : 'text.primary',
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={item.label}
                            sx={{
                                opacity: isExpanded ? 1 : 0,
                                transition: 'opacity 0.2s ease-in-out',
                                '& .MuiListItemText-primary': {
                                    fontWeight: isActive ? 600 : 400,
                                    color: isActive ? 'primary.main' : 'text.primary'
                                }
                            }}
                        />
                    </ListItemButton>
                </Tooltip>
            </ListItem>
        );
    };

    const renderUserProfile = () => (
        <Box
            sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                minHeight: 80
            }}
        >
            <Avatar
                sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'primary.main'
                }}
            >
                <AccountIcon />
            </Avatar>
            {isExpanded && (
                <Box sx={{
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out',
                    overflow: 'hidden'
                }}>
                    <Typography variant="subtitle2" noWrap>
                        John Doe
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                        Administrator
                    </Typography>
                </Box>
            )}
        </Box>
    );

    return (
        <Drawer
            variant="permanent"
            open={isExpanded}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                width: {
                    xs: isExpanded ? SIDEBAR_WIDTH_EXPANDED_XS : SIDEBAR_WIDTH_COLLAPSED_XS,
                    md: isExpanded ? SIDEBAR_WIDTH_EXPANDED_MD : SIDEBAR_WIDTH_COLLAPSED_MD
                },
                flexShrink: 0,
                whiteSpace: 'nowrap',
                transition: 'width 0.3s ease-in-out',
                display: { xs: 'none', sm: 'block' },
                '& .MuiDrawer-paper': {
                    width: {
                        xs: isExpanded ? SIDEBAR_WIDTH_EXPANDED_XS : SIDEBAR_WIDTH_COLLAPSED_XS,
                        md: isExpanded ? SIDEBAR_WIDTH_EXPANDED_MD : SIDEBAR_WIDTH_COLLAPSED_MD
                    },
                    transition: 'width 0.3s ease-in-out',
                    overflowX: 'hidden',
                    boxShadow: { xs: '1px 0 4px rgba(0,0,0,0.08)', md: '2px 0 8px rgba(0,0,0,0.1)' },
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: '#ECEAFC'
                },
            }}
        >
            {/* Header with toggle button */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isExpanded ? 'space-between' : 'center',
                    p: { xs: 1.5, sm: 2 },
                    minHeight: { xs: 56, sm: 64 },
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {isExpanded && (
                    <img
                        src="/doca_logo.png"
                        alt="Logo"
                        style={{
                            height: 'auto',
                            maxHeight: 64,
                            width: 'auto',
                            opacity: isExpanded ? 1 : 0,
                            transition: 'opacity 0.2s ease-in-out'
                        }}
                    />
                )}
                <IconButton
                    onClick={onToggle}
                    sx={{
                        transition: 'transform 0.2s ease-in-out',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                        '& .MuiSvgIcon-root': {
                            fontSize: { xs: 20, sm: 22, md: 24 }
                        }
                    }}
                >
                    {isExpanded ? <ChevronLeftIcon /> : <MenuIcon />}
                </IconButton>
            </Box>

            {/* User Profile */}
            {/* {renderUserProfile()} */}

            {/* Navigation Items */}
            <List sx={{ flexGrow: 1, py: 1 }}>
                {navigationItems.map(renderNavigationItem)}
            </List>


            {/* Logout Button */}
            <Box sx={{ px: 1, mb: { xl: 8, lg: 6, md: 3 } }}>
                <Tooltip title={!isExpanded ? "Logout" : ""} placement="right" arrow>
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            minHeight: 44,
                            justifyContent: isExpanded ? 'initial' : 'center',
                            px: 2,
                            borderRadius: 2,
                            '&:hover': {
                                backgroundColor: 'action.hover',
                                '& .MuiListItemIcon-root': {
                                    color: 'error.main',
                                },
                                '& .MuiListItemText-primary': {
                                    color: 'error.main',
                                    fontWeight: 600,
                                },
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: isExpanded ? 3 : 'auto',
                                justifyContent: 'center',
                                color: 'text.primary',
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            <LogoutIcon />
                        </ListItemIcon>

                        <ListItemText
                            primary="Logout"
                            sx={{
                                opacity: isExpanded ? 1 : 0,
                                transition: 'opacity 0.2s ease-in-out',
                            }}
                        />
                    </ListItemButton>
                </Tooltip>
            </Box>


            {/* Footer */}
            <Box
                sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'center'
                }}
            >
                {isExpanded && (
                    <Typography variant="caption" color="text.secondary" sx={{
                        transition: 'opacity 0.2s ease-in-out',
                    }}>
                        <Link
                            href="https://iitk.ac.in/cdis/"
                            target="_blank"
                            rel="noopener noreferrer"
                            color="inherit"
                            underline="hover"
                            sx={{ fontWeight: 700 }}
                        >
                            @ CDIS, IITK
                        </Link>
                    </Typography>
                )}
            </Box>
        </Drawer>
    );
};

export default Sidebar;